import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { APP } from 'app/AppRoutes';
import { useSelector, useDispatch } from 'react-redux';
import {
  Modal,
  FormGroup,
  RadioButtonGroup,
  RadioButton,
  InlineLoading
} from 'carbon-components-react';
import {
  selectUser,
  selectUpdatingUser,
  selectUsersLoading,
  selectShowNotificationOptionsModal
} from 'redux/user/selector';
import {
  updateUserStart,
  updateShowNotificationOptionsModal
} from 'redux/user/actions';

export const NotificationOptionsModal: React.FC<Props> = () => {
  const dispatch: any = useDispatch();
  const user = useSelector(selectUser);
  const userLoading = useSelector(selectUsersLoading);
  const userUpdating = useSelector(selectUpdatingUser);
  const showNotificationOptionsModal: boolean = useSelector(
    selectShowNotificationOptionsModal
  );
  const [selectedRadioValue, setSelectedRadioValue] = useState<string>('');
  const [disableButtons, setDisableButtons] = useState<boolean>(false);
  const [primaryButtonText, setPrimaryButtonText] = useState<any>('Save');

  const resetForm = useCallback(() => {
    setDisableButtons(false);
    dispatch(updateShowNotificationOptionsModal(false));
  }, [dispatch]);

  const submitForm = () => {
    setDisableButtons(true);
    dispatch(
      updateUserStart({
        preferences: { notificationSetting: selectedRadioValue }
      })
    );
  };

  const onChangeHandler = (val: string) => {
    setSelectedRadioValue(val);
  };

  useEffect(() => {
    if (!userLoading && showNotificationOptionsModal && !userUpdating) {
      setSelectedRadioValue(
        selectedRadioValue
          ? selectedRadioValue
          : user.preferences.notificationSetting || 'ALL_RECEIVED'
      );
    }
  }, [
    user,
    userLoading,
    showNotificationOptionsModal,
    userUpdating,
    selectedRadioValue
  ]);

  useEffect(() => {
    const buttonStateActive = (
      <InlineLoading description={'Saving'} status={'active'} />
    );
    const buttonStateFinished = (
      <InlineLoading description={'Saved'} status={'finished'} />
    );
    if (userUpdating) {
      setDisableButtons(true);
      setPrimaryButtonText(buttonStateActive);
    } else if (userUpdating === false) {
      setPrimaryButtonText(buttonStateActive);
      setTimeout(() => {
        setPrimaryButtonText(buttonStateFinished);
      }, 2000);
      setTimeout(() => {
        setPrimaryButtonText('Save');
        resetForm();
      }, 4000);
    }
    // eslint-disable-next-line
  }, [userUpdating]);

  const RadioButtonFirstChoice = React.forwardRef(() => (
    <div className="first-choice-wrapper">
      <div className="first-choice-option">
        <div className="first-choice-option-text">
          Notify me when all responses are received (default)
        </div>
      </div>

      <div className="first-choice-helper-text">
        You will still receive a notification if any responses are significantly
        delayed
      </div>
    </div>
  ));

  const RadioButtonDisableAll = React.forwardRef(() => (
    <div className="first-choice-wrapper">
      <div className="first-choice-option">
        <div className="first-choice-option-text">
          Disable all email notifications
        </div>
      </div>
    </div>
  ));

  return ReactDOM.createPortal(
    <Modal
      open={showNotificationOptionsModal}
      modalHeading="Response notification options"
      onRequestClose={() => resetForm()}
      primaryButtonText={primaryButtonText}
      secondaryButtonText={'Cancel'}
      onRequestSubmit={() => submitForm()}
      primaryButtonDisabled={disableButtons}
      className="notification-options-modal"
    >
      <div className="notification-options-modal-sub-header">
        Choose when to be notified of enquiry responses
      </div>
      <div className="notification-options-modal-form">
        <FormGroup
          legendText="When would you like to be notified?"
          className="notification-options-modal-form-group"
        >
          <RadioButtonGroup
            name="notificationOptions"
            orientation="vertical"
            valueSelected={selectedRadioValue}
            onChange={(val: string) => {
              onChangeHandler(val);
            }}
          >
            <RadioButton
              id="notificationOptions-1"
              labelText={<RadioButtonFirstChoice />}
              value="ALL_RECEIVED"
            />
            <RadioButton
              id="notificationOptions-2"
              labelText="Notify me on every response received"
              value="NOTIFY_RESPONSE_RECEIVED"
            />
            <RadioButton
              id="notificationOptions-3"
              labelText="Forward each response to me as it is received"
              value="FORWARD_ALL_RESPONSE"
            />
            <RadioButton
              id="notificationOptions-4"
              labelText={<RadioButtonDisableAll />}
              value="DISABLE_ALL_RESPONSE"
            />
          </RadioButtonGroup>
        </FormGroup>
      </div>
      <div className="notification-options-modal-footer">
        Note: Responses are automatically deleted 90 days after the date they
        were received
      </div>
    </Modal>,
    document.querySelector(`#${APP}`) || document.body
  );
};

type ownProps = {};

type Props = ownProps;

export default React.memo(NotificationOptionsModal);
